import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { countriesService } from "../../api/countriesService/countriesService";
import { fleetService } from "../../api/fleetService/fleetService";
import { profileService } from "../../api/profileService/profileService";
import { rolesService } from "../../api/rolesService/rolesService";
import { routesService } from "../../api/routesService/routesService";
import { servicesService } from "../../api/servicesService/servicesService";
import { IsAuthorized } from "../../utils/helpers/auth";
import { FleetContext } from "../../context/fleetContext";
import { ServicesContext, IServicesContext } from "../../context/servicesContext";
import { RoutesContext, IRoutesContext } from "../../context/routesContext";
import { RolesContext } from "../../context/rolesContext";
import { IFleetContext } from "../../types/fleet";
import { IRolesContext } from "../../types/roles";
import { useContext } from "react";
import { computeCountries } from "../helpers/computeCountries";
import { groupServicesInfo } from "../helpers/groupServicesInfo";
import { getApiCallsForPath, ApiCallType } from "../../config/apiCallConfig";
import { routeUrls } from "../../config/routeUrls";

export const useAsyncDataLoader = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Context setters
  const fleetContext = useContext(FleetContext) as IFleetContext;
  const servicesContext = useContext(ServicesContext) as IServicesContext;
  const routesContext = useContext(RoutesContext) as IRoutesContext;
  const rolesContext = useContext(RolesContext) as IRolesContext;

  const {
    setVehicles,
    setEmissionClass,
    setEngineType,
    setVehiclesInfo,
    setIsIntermodal,
    setLastUpdate,
    vehicles,
    emissionClass,
    engineType,
    vehiclesInfo,
    isIntermodal,
    lastUpdate
  } = fleetContext;

  const { setServices, services } = servicesContext;
  const { setRoutes, routes } = routesContext;
  const { setRoles, roles } = rolesContext;

  useEffect(() => {
    if (!IsAuthorized()) {
      setIsLoading(false);
      return;
    }

    // Controlla se siamo nella dashboard
    const isDashboard = location.pathname === routeUrls.dashboard;
    
    // Se siamo nella dashboard, salta completamente tutte le chiamate
    if (isDashboard) {
      console.log('Dashboard detected - skipping all API calls in useAsyncDataLoader');
      setIsLoading(false);
      return;
    }

    // Ottieni le chiamate API necessarie per il path corrente
    const neededApiCalls = getApiCallsForPath(location.pathname);
    
    setIsLoading(true);
    setError(null);

    // Controlla se i dati sono già presenti prima di fare le chiamate
    const checkAndCall = (apiCall: ApiCallType, apiFunction: () => Promise<any>, errorMessage: string, dataCheck: () => boolean) => {
      if (neededApiCalls.includes(apiCall)) {
        if (dataCheck()) {
          // I dati sono già presenti, non fare la chiamata
          console.log(`Skipping ${apiCall} - data already available`);
        } else {
          // I dati non sono presenti, fai la chiamata in modo indipendente
          apiFunction()
            .then((response) => {
              if (response.success) {
                // Aggiorna i context con i dati ricevuti
                switch (apiCall) {
                  case 'services':
                    setServices(response.responseBody);
                    break;
                  case 'routes':
                    setRoutes(response.responseBody);
                    break;
                  case 'roles':
                    setRoles(response.responseBody.roles || []);
                    break;
                  case 'fleet':
                    // Questa è la chiamata getFleet(), non getFleetInfo()
                    setVehicles(response.responseBody.fleet || []);
                    setEmissionClass(response.responseBody.emissionClass || []);
                    setEngineType(response.responseBody.engineType || []);
                    setLastUpdate(response.responseBody.lastUpdate || null);
                    break;
                  case 'countries':
                    // Countries non ha setter specifico, ma viene gestito dal context
                    break;
                }
                console.log(`${apiCall} API completed independently`);
                return response;
              }
              throw new Error(response.message || errorMessage);
            })
            .catch((error) => {
              console.error(`Error in ${apiCall}:`, error);
              setError(errorMessage);
            });
        }
      }
    };

    // Chiamata servizi
    checkAndCall('services', 
      () => servicesService.services(),
      'Errore caricamento servizi',
      () => Boolean(services && services.mainServices && services.mainServices.length > 0)
    );

    // Chiamata servicesInfo
    checkAndCall('servicesInfo',
      () => servicesService.serviceInfo(),
      'Errore caricamento servicesInfo',
      () => Boolean(services && services.additionalServices && services.additionalServices.length > 0)
    );

    // Chiamata routes
    checkAndCall('routes',
      () => routesService.getRoutes(),
      'Errore caricamento routes',
      () => Boolean(routes && routes.routes && routes.routes.length > 0)
    );

    // Chiamata roles
    checkAndCall('roles',
      () => rolesService.getRoles(),
      'Errore caricamento ruoli',
      () => Boolean(roles && roles.length > 0)
    );

    // Chiamata fleet
    checkAndCall('fleet',
      () => fleetService.getFleet(),
      'Errore caricamento fleet',
      () => {
        return !!(vehicles && vehicles.length > 0 && emissionClass && emissionClass.length > 0 && engineType && engineType.length > 0);
      }
    );

    // Chiamata fleetInfo (parte di fleet) - indipendente
    if (neededApiCalls.includes('fleet')) {
      if (vehiclesInfo && vehiclesInfo.length > 0 && isIntermodal !== null && isIntermodal !== undefined) {
        console.log('Skipping fleetInfo - data already available');
      } else {
        fleetService.getFleetInfo()
          .then((fleetInfoRes) => {
            if (fleetInfoRes.success) {
              setVehiclesInfo(fleetInfoRes.responseBody.vehicles || []);
              setIsIntermodal(fleetInfoRes.responseBody.isIntermodal || false);
              console.log('FleetInfo API completed independently');
            }
          })
          .catch((error) => {
            console.error('Error in fleetInfo:', error);
            setError('Errore caricamento fleetInfo');
          });
      }
    }

    // Chiamata countries
    checkAndCall('countries',
      () => countriesService.countries(),
      'Errore caricamento countries',
      () => false // Sempre carica countries per ora
    );

    // Imposta loading a false dopo un breve delay per permettere alle chiamate di iniziare
    setTimeout(() => {
      setIsLoading(false);
    }, 100);

    console.log(`Loading API calls for path ${location.pathname}:`, neededApiCalls);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return {
    isLoading,
    error,
    reloadData: () => {} // reload non implementato qui
  };
}; 